import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  AttachedFile,
  Button,
  HistoryQuestion,
  Label,
  RadioGroupForm,
  RecordVideoForm
} from '@/components';
import {
  EmailInput,
  PhoneInput,
  CheckboxGroupForm,
  AddressBox,
  TextList,
  CountrySelect,
  DocumentNumberInput
} from '@/modules/kycModule/Fields';
import { FormDatePicker, FormInput } from '@/modules/kycModule/components';
import { Container, Body4Light } from '@/ui';
import { Flex } from 'antd';
import { Label2, Body1Light } from '@/ui/typography';
import { FileUploadForm } from '@/modules/kycModule/Fields/FileUpload';
import { useMutation } from '@tanstack/react-query';
import { adminKYCApi } from '@/api';
import { COLORS } from '@/utils/static';
import { RejectModal } from '@/modules/Questionnaire/modals';
import { SignatureForm } from '@/modules/kycModule/Fields/SignatureForm';
import { useWatch } from 'react-hook-form';
import { ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN, STATUS } from '@/utils/static/kyc';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { getLocalizedText, sumFloat } from '@/utils/helpers';
import { UrlLinkButton } from '@/modules/Sumsub/UrlLinkButton';
import { usePopups } from '@/hooks';
import { Notes } from '@/components/admin/molecules';
import { CheckCircleOutlined } from '@ant-design/icons';

let isRequiredSave = '';
export const Question = ({
  name,
  question,
  control,
  watch,
  setValue,
  trigger,
  submitCount,
  adminView,
  rejectedQuestions,
  addRejectedQuestion,
  questionnaireId,
  questionnaireStatus,
  preFilledQuestions,
  disableAll,
  disableQuestion,
  isSelectScoringTooltip,
  isReadTooltip,
  creators,
  refetchQuest,
  isVeriffQuestionnaire = false,
  sumSubStatusRes,
  refetchSumSubRes
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(
    question && question?.visibleOperator ? question?.visibleOperator === 'AND' : true
  );
  const fields = useWatch({
    control,
    name: question.notRequiredConditions ? question.notRequiredConditions.map((item) => item) : null
  });
  const fieldsSumConditions = useWatch({
    control,
    name: question.sumConditions ?? []
  });

  //isVisible logic
  if (question && question.visibleConditions && Array.from(question.visibleConditions).length > 0) {
    const conditions = question.visibleConditions;
    const operator = question.visibleOperator;
    const swap = question.visibleSwap;
    let fields = watch(conditions.map((item) => item.id));

    let isVisible = operator === 'AND';
    for (const [index, value] of fields.entries()) {
      if (
        swap === true &&
        operator === 'AND' &&
        (Array.isArray(value)
          ? value.includes(conditions[index].value)
          : conditions[index].value === value)
      ) {
        isVisible = false;
        break;
      }
      if (
        swap === false &&
        operator === 'AND' &&
        (Array.isArray(value)
          ? !value.includes(conditions[index].value)
          : conditions[index].value !== value)
      ) {
        isVisible = false;
        break;
      }
      if (
        swap === false &&
        operator === 'OR' &&
        (Array.isArray(value)
          ? value.includes(conditions[index].value)
          : conditions[index].value === value)
      ) {
        isVisible = true;
        break;
      }
    }

    if (visible !== isVisible) setVisible(isVisible);
  }

  //isRequiredLogic
  const isRequired = useMemo(() => {
    if (adminView || !question || !question.isRequired) return null;

    if (!question.notRequiredConditions) return t('Required');

    if (question.notRequiredConditions) {
      const triggerFields = () => {
        setTimeout(() => {
          trigger([question.id, ...question.notRequiredConditions]);
        }, 500);
      };

      let returnValue = t('One of the fields is required');

      for (const [index, value] of fields.entries()) {
        if (value) returnValue = null;
      }

      if (submitCount > 0 && isRequiredSave !== '' && isRequiredSave !== returnValue)
        triggerFields();

      return returnValue;
    }
  }, [fields, submitCount]);

  useEffect(() => {
    isRequiredSave = isRequired;
  }, [isRequired]);

  useEffect(() => {
    if (fieldsSumConditions && fieldsSumConditions.length > 0 && setValue) {
      const sum = sumFloat(fieldsSumConditions);
      const oldAnswer = question.answers?.find((item) => +item.answer === sum)?.answer;
      setValue(name || question.id, sum, { shouldDirty: +oldAnswer !== sum });
    }
  }, [fieldsSumConditions, setValue, question]);

  const RenderedComponent = useMemo(
    () => getRenderedComponent(question.type, adminView),
    [question.type, adminView]
  );

  const validateByType = useMemo(
    () => ({
      CONFIRMATIONS: (v) => v.length === question.availableAnswers.length || t('Confirm all items'),
      INPUT: (v) =>
        question.maxCountSymbols &&
        question.minCountSymbols &&
        question.countFirstLetterSymbolsCountry
          ? v.length < question.maxCountSymbols &&
            v.length > question.minCountSymbols &&
            v.match(new RegExp('^[A-Za-z]{' + question.countFirstLetterSymbolsCountry + '}'))
            ? true
            : t('Min - 15, max - 34, first two symbols must be letters')
          : true
    }),
    []
  );

  const answerCreator = creators[question?.id];

  const rejectedComment = useMemo(() => {
    return rejectedQuestions ? rejectedQuestions[question.id] : null;
  }, [rejectedQuestions, question]);

  const isDisabled = useMemo(() => {
    return (
      (disableAll && !rejectedComment) ||
      adminView ||
      disableQuestion ||
      preFilledQuestions[question?.id]
    );
  }, [disableAll, rejectedComment, adminView, disableQuestion, preFilledQuestions, question]);

  if (RenderedComponent && visible) {
    const validations = validateByType[question.type];

    return (
      <Container>
        {/*{question.question && <Body2Light>{question.question}</Body2Light>}*/}
        <RenderedComponent
          form={{
            name: name || question.id,
            control,
            rules: {
              required: isRequired,
              validate: validations,
              ...(question.onlyLatin && {
                pattern: {
                  value: /^[a-zA-Z0-9 ()*_\-!#$%^&*,."\'\][]*$/,
                  message: 'Only latin characters'
                }
              })
            }
          }}
          label={t(`${question.question}`)}
          question={question}
          tooltip={question.tooltip}
          tooltip_RU={question.tooltip_RU}
          tooltip_LV={question.tooltip_LV}
          adminView={adminView}
          disabled={isDisabled}
          {...(question.type === 'INPUT' ? { clearRegex: question.clearRegex } : {})}
          {...(question.type === 'RADIO' || question.type === 'CHECKBOX'
            ? { isSelectScoringTooltip: isSelectScoringTooltip }
            : {})}
          {...(question.type === 'DOC_UPLOAD' ? { isReadTooltip: isReadTooltip } : {})}
          required={isRequired}
          {...(question.type === 'URL_LINK' ? { isVeriffQuestionnaire: true } : {})}
          {...(question.type === 'URL_LINK' ? { sumSubStatusRes: sumSubStatusRes } : {})}
          {...(question.type === 'URL_LINK' ? { refetchSumSubRes: refetchSumSubRes } : {})}
        />

        {answerCreator && (
          <Body4Light $marginTop={10} $color={COLORS.aragonDarkBlue}>
            Filled by {answerCreator.firstName} {answerCreator.lastName}
          </Body4Light>
        )}

        {question?.answers?.[0]?.creatorAdmin && (
          <Body4Light $marginTop={10} $color={COLORS.orange}>
            {t('Question was edited by administrator')}
          </Body4Light>
        )}

        {rejectedComment && (
          <Body1Light $marginTop={8} $color={COLORS.red} $inline>
            {rejectedComment}
          </Body1Light>
        )}

        {question.type !== 'TEXT' && (
          <Flex
            justify={adminView && !isVeriffQuestionnaire ? 'space-between' : 'flex-end'}
            align='center'
          >
            {adminView && addRejectedQuestion && (
              <AdminRejectButton
                questionId={question.id}
                addRejectedQuestion={addRejectedQuestion}
                rejected={rejectedComment}
                questionnaireId={questionnaireId}
                question={question}
                disabled={!ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[questionnaireStatus]}
              />
            )}
            <Flex gap={8} align='center'>
              {adminView && question?.answers?.[0]?.isFixed && (
                <Tooltip title={t('Answer was fixed by client')}>
                  <CheckCircleOutlined
                    style={{ color: COLORS.mintGreen, fontSize: 24, marginTop: 6 }}
                  />
                </Tooltip>
              )}

              {adminView && !isVeriffQuestionnaire && (
                <Notes
                  notes={question.notes}
                  refetchQuest={refetchQuest}
                  questionId={question.id}
                  questionTitle={question.question}
                />
              )}

              {(question?.answers?.length > 0 || question?.answersOld?.length > 0) && (
                <Flex>
                  <HistoryQuestion
                    questionTitle={question.question}
                    questionType={question.type}
                    answers={question?.answers}
                    availableAnswers={question?.availableAnswers}
                    answersOld={question?.answersOld}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
      </Container>
    );
  }

  return null;
};

const AdminRejectButton = ({ questionId, rejected, addRejectedQuestion, disabled, question }) => {
  const { t } = useTranslation();
  const { showErrorToast } = usePopups();
  const { id, questionnaireId } = useParams();
  const [isModalActive, setModalActive] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const { mutate: rejectQuestion } = useMutation({
    queryFn: (comment) =>
      adminKYCApi.setQuestionnaireStatus({
        investorId: id,
        status: STATUS.REJECTED,
        questionId: questionId,
        questionnaireId: questionnaireId,
        comment: comment
      })
  });

  const onReject = async () => {
    try {
      if (comment) {
        await rejectQuestion(comment);
        addRejectedQuestion(questionId, comment);
        setModalActive(false);
      } else {
        setError(t('Required'));
      }
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };

  return (
    <Container $marginTop={8}>
      <Button
        text={rejected ? t('Rejected') : t('Reject question')}
        danger
        onClick={() => setModalActive(true)}
        disabled={disabled || rejected}
      />

      <RejectModal
        active={isModalActive}
        onClose={() => setModalActive(false)}
        comment={comment}
        setComment={setComment}
        error={error}
        onSubmit={onReject}
      />
    </Container>
  );
};

const FormatRadioGroupForm = ({
  question,
  adminView,
  required,
  isSelectScoringTooltip,
  ...props
}) => {
  const { availableAnswers, answers, ...questionRest } = question;
  const { i18n } = useTranslation();

  return (
    <Container>
      {question.question && (
        <Label
          required={required}
          label={question.question}
          tooltip={question.tooltip ? getLocalizedText(question, 'tooltip', i18n) : null}
        >
          {question.tooltip && <Tooltip text={getLocalizedText(question, 'tooltip', i18n)} />}
          <RadioGroupForm
            items={availableAnswers ? availableAnswers.map((item) => item.answer) : []}
            ids={availableAnswers ? availableAnswers.map((item) => item.id) : []}
            pointsScoring={
              availableAnswers ? availableAnswers.map((item) => item.pointsScoring) : []
            }
            points={availableAnswers ? availableAnswers.map((item) => item.points) : []}
            isSelectScoringTooltip={isSelectScoringTooltip}
            {...questionRest}
            {...props}
          />
        </Label>
      )}
    </Container>
  );
};

const RenderFileView = ({ label, tooltip, tooltip_RU, tooltip_LV, form }) => {
  const { i18n } = useTranslation();
  const nameArray = form.name.split('.');

  const getValue = (values, nameArray) => {
    if (values) {
      return nameArray.length === 1
        ? values[nameArray[0]]
        : nameArray.reduce((result, current) => result[current], values);
    }

    return null;
  };

  try {
    return (
      <div>
        {label && (
          <Label2>
            {label}
            {tooltip && (
              <Tooltip
                text={getLocalizedText(
                  {
                    tooltip: tooltip,
                    tooltip_RU: tooltip_RU,
                    tooltip_LV: tooltip_LV
                  },
                  'tooltip',
                  i18n
                )}
              />
            )}
          </Label2>
        )}
        <AttachedFile url={getValue(form?.control['_formValues'], nameArray)} />
      </div>
    );
  } catch (e) {}
};

const getRenderedComponent = (type, isAdmin) => {
  const component = isAdmin ? adminComponentByType[type] : componentByType[type];
  return component || FormInput;
};
const componentByType = {
  EMAIL: EmailInput,
  PHONE: PhoneInput,
  INPUT: FormInput,
  AGENT_CODE: FormInput,
  NUMBER: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  FLOAT: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  SUM: (props) => <FormInput {...props} disabled />,
  COUNTRY: CountrySelect,
  ADDRESS: AddressBox,
  RADIO: FormatRadioGroupForm,
  CHECKBOX: CheckboxGroupForm,
  CONFIRMATIONS: CheckboxGroupForm,
  DOC_UPLOAD: FileUploadForm,
  RECORD_VIDEO: RecordVideoForm,
  DATE: FormDatePicker,
  SIGNATURE: SignatureForm,
  TEXT: TextList,
  URL_LINK: UrlLinkButton,
  DOCUMENT_NUMBER: DocumentNumberInput
};
const adminComponentByType = {
  EMAIL: FormInput,
  PHONE: PhoneInput,
  INPUT: FormInput,
  AGENT_CODE: FormInput,
  NUMBER: FormInput,
  FLOAT: FormInput,
  SUM: FormInput,
  COUNTRY: FormInput,
  ADDRESS: AddressBox,
  RADIO: FormatRadioGroupForm,
  CHECKBOX: CheckboxGroupForm,
  CONFIRMATIONS: CheckboxGroupForm,
  DATE: FormDatePicker,
  SIGNATURE: RenderFileView,
  DOC_UPLOAD: RenderFileView,
  RECORD_VIDEO: RenderFileView,
  TEXT: TextList,
  URL_LINK: UrlLinkButton,
  DOCUMENT_NUMBER: DocumentNumberInput
};
